import {
  CheckIcon,
  ChevronDownIcon,
  Cross1Icon,
  MagnifyingGlassIcon,
} from "@radix-ui/react-icons";
import {
  Button,
  Flex,
  IconButton,
  Popover,
  ScrollArea,
  Spinner,
  Text,
  TextField,
  Theme,
} from "@radix-ui/themes";
import * as React from "react";

import { useUpdateEffect } from "react-use";
import { twMerge } from "tailwind-merge";
import { useStaffAnnotatorsQuery } from "@/shared/api/gateway-api";
import { Command } from "cmdk";
import { cn } from "@/shared/utils/cn";

export function AssigneeCombobox({
  defaultValue,
  disabled = false,
  onValueChange,
}: {
  defaultValue?: string;
  disabled?: boolean;
  onValueChange?: (
    value: string | undefined,
    option?: {
      value: string | undefined;
      label: string;
    }
  ) => void;
}) {
  const [search, setSearch] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState<string | undefined>(defaultValue);

  useUpdateEffect(() => {
    const selectedOption = options.find((option) => option.value === value);
    onValueChange?.(value, selectedOption);
  }, [value]);

  const { data: annotators, isFetching } = useStaffAnnotatorsQuery();

  const options = React.useMemo(() => {
    const assignees =
      annotators?.results.map((annotator) => ({
        value: annotator.id,
        label: annotator.first_name + " " + annotator.last_name,
      })) || [];

    const noneOption = {
      value: "null",
      label: "None",
    };

    return [noneOption].concat(assignees);
  }, [annotators]);

  const filteredOptions = React.useMemo(() => {
    if (!search) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const displayValue = React.useMemo(() => {
    const selectedOption = options.find((option) => option.value === value);

    if (!value || !selectedOption) {
      return "Select Assignee";
    }

    return selectedOption.label;
  }, [value, options]);

  const handleSelect = (option: (typeof options)[0]) => () => {
    setValue(option.value === value ? undefined : option.value);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <Button
          disabled={disabled}
          variant="outline"
          size="1"
          color="gray"
          radius="full"
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={`Select assignee. Current selection: ${displayValue}`}
          className="justify-between"
        >
          {displayValue}
          <Flex className="ml-2" gap="1">
            {!!value && (
              <IconButton
                size="1"
                className="w-4 h-4"
                variant="soft"
                onClick={(e) => {
                  e.stopPropagation();
                  setValue(undefined);
                  setOpen(false);
                }}
              >
                <Cross1Icon width={12} height={12} />
              </IconButton>
            )}
            <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50" />
          </Flex>
        </Button>
      </Popover.Trigger>
      <Popover.Content className="min-w-[240px] p-0 shadow-lg border border-gray-200 bg-white rounded-lg overflow-hidden">
        <Theme>
          <Command shouldFilter={false}>
            <Command.Input asChild>
              <TextField.Root
                value={search}
                onChange={(e) => {
                  e.stopPropagation();
                  setSearch(e.target.value);
                }}
                size="2"
                placeholder="Search assignee..."
                className="m-2"
              >
                <TextField.Slot>
                  <MagnifyingGlassIcon height="16" width="16" />
                </TextField.Slot>
              </TextField.Root>
            </Command.Input>

            <ScrollArea className="max-h-[320px] px-2 pb-2">
              <Command.List className="space-y-1">
                {isFetching && (
                  <div className="relative min-h-[100px]">
                    <Flex
                      className="absolute inset-0 z-20 bg-white/95 backdrop-blur-sm rounded-md"
                      align="center"
                      justify="center"
                      gap="2"
                    >
                      <Spinner size="3" />
                      <Text
                        as="p"
                        color="gray"
                        size="2"
                        className="font-medium"
                      >
                        Loading assignees...
                      </Text>
                    </Flex>
                  </div>
                )}

                {filteredOptions.length === 0 ? (
                  <Command.Empty>
                    <div className="py-8 text-center">
                      <Text size="2" color="gray">
                        No assignees found.
                      </Text>
                    </div>
                  </Command.Empty>
                ) : (
                  filteredOptions?.map((option) => (
                    <Command.Item
                      key={option.value}
                      value={option.label}
                      onSelect={handleSelect(option)}
                      asChild
                    >
                      <Flex
                        align="center"
                        gap="3"
                        className={twMerge([
                          "px-3 py-2.5 outline-none hover:bg-blue-600 hover:text-white rounded-md cursor-pointer transition-colors data-[selected='true']:bg-blue-600 data-[selected='true']:text-white",
                          value === option.value &&
                            "bg-indigo-50 hover:bg-indigo-600 hover:text-white data-[selected='true']:bg-indigo-600 data-[selected='true']:text-white",
                        ])}
                      >
                        <CheckIcon
                          className={cn(
                            "h-4 w-4 text-indigo-600",
                            value === option.value ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <Text size="2" className="font-medium">
                          {option.label}
                        </Text>
                      </Flex>
                    </Command.Item>
                  ))
                )}
              </Command.List>
            </ScrollArea>
          </Command>
        </Theme>
      </Popover.Content>
    </Popover.Root>
  );
}
